import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import type { AuthMe } from "@/api/types";
import {
  useAuthMe,
  useLogout,
  usePendingInvitations,
} from "@/api/queries";
import { useTenantBranding } from "@/api/queries/branding";
import {
  useIsCurrentOrgAdmin,
  useIsCurrentOrgOperator,
} from "@/api/queries/orgs";
import { useAccent } from "@/components/AccentProvider";
import { AccentSwatches } from "@/components/AccentSwatches";
import { LangSwitcher } from "@/components/LangSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
// Přímo z modulu, ne přes barrel `@/help` (KAN-204): barrel re-exportuje
// i `helpDeepLink`, které kvůli jedné funkci táhne celý `adminNav.ts`
// (45 kB) do entry chunku — AccountMenu visí na eager cestě App → Header.
import { HelpToggle } from "@/help/HelpToggle";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { ApiError } from "@/lib/api";
import { cx } from "@/lib/utils";
import { RawJsonToggle } from "@/rawJson";
import { useShellNav, type ShellNav } from "@/shell/useShellNav";
import { VOCABULARY_MODES, useVocabulary } from "@/lib/vocabulary";
import {
  buildPlatformAdminUrl,
  buildPlatformUrl,
  tenantHopHref,
} from "@/shell/resolveShell";

/**
 * The one account menu — storefront, tenant admin and platform admin.
 *
 * Anonymous → a "Sign in" link. Signed-in → an email-labeled dropdown
 * with six sections, in this order:
 *
 *   1. identity      role caption + email
 *   2. notices       pending invitations (only when there are any)
 *   3. context       Administrace / Dílna / Obchod / teams / platform admin
 *   4. personal      Můj účet (+ orders & projects where those routes exist)
 *   5. preferences   theme, accent, language, vocabulary, inline help, raw JSON
 *   6. sign out
 *
 * Every preference in section 5 is stored **on the account**, never in the
 * browser: ``ui_theme``, ``ui_accent``, ``preferred_lang``,
 * ``ui_stock_vocabulary``, ``show_inline_help``, ``show_raw_json``. That is
 * the whole point of the section — a person who sets up their console on a
 * laptop finds it the same on a tablet at the machine. localStorage appears
 * only as an anti-flash mirror for theme and accent, which have to be right
 * before the first paint.
 *
 * 🪤 **Org switching is NOT duplicated here.** The tenant shell has
 * ``OrgSwitcher`` in its topbar and that is a different animal from these
 * entries: it POSTs ``/auth/switch-org``, records an audit event and carries
 * a page hint so the operator keeps their page across the hop. The
 * ``Otevřít … →`` entries below are plain cross-host links, rendered only on
 * the shells that mount no switcher (marketing, platform console). Building
 * a second switcher in here would mean two ways to change org with different
 * audit behaviour.
 *
 * Until 2026-08-09 this was two components: this one (storefront +
 * marketing) and ``AdminUserMenu`` inside ``AdminLayout``. They drifted —
 * theme/help/raw-JSON only existed in the admin copy, invitations and the
 * Dílna entry only in this one, and NEITHER offered a way into
 * ``admin.forgmatic.com``: a platform admin had to type the hostname.
 * One component means the next item lands in all three shells at once.
 *
 * What still differs is only what a shell can actually reach — the
 * platform-admin shell mounts no ``/account/orders`` — so section 3 and 4
 * branch on ``shellNav.accountMenuVariant``. The marketing shell DOES mount
 * ``/account`` since KAN-81, which is why the no-membership entry below is a
 * plain ``<Link>`` rather than a jump into the demo tenant.
 *
 * Cross-host entries are ``<a>``, never ``<Link>`` — and they go through
 * ``tenantHopHref`` (the ``/auth/hop`` interstitial), NOT straight at the
 * target. The session cookie stopped spanning hosts in #3145: it is host-only
 * outside the platform zone, so a direct link arrives signed out. This
 * paragraph claimed ``COOKIE_DOMAIN`` carried it until KAN-81, which is
 * exactly the belief that left nine of these links bare.
 *
 * They also carry ``target="_blank"``: every one of them ends on ANOTHER
 * host, and a plain click used to replace the console the operator was
 * standing in — a platform admin comparing two tenants lost their place on
 * each hop. Note the ``href`` is same-origin (``/auth/hop``), so nothing in
 * the markup looks cross-host; the jump happens inside the interstitial via
 * ``window.location.replace``. That is why the attribute cannot be derived
 * from the href and has to be spelled out at each call site.
 *
 * Only renders the dropdown once we've resolved the auth state — the
 * initial render is a tiny placeholder so the header doesn't flash
 * from "signed-in email" to "Sign in" (or vice-versa) across
 * navigations. We rely on ``useAuthMe`` which is already cached for
 * 60 s; subsequent renders across pages hit the cache and the menu
 * is effectively instant.
 *
 * ``useAuthMe`` returns a 401 ApiError for anonymous users — that's
 * the success-of-recon, not a bug — so we treat ``isError`` with
 * ``ApiError status=401`` as "anon" rather than surfacing it as an
 * actual error.
 */

/**
 * One component, one ``data-testid`` vocabulary: ``account-menu-*`` on
 * every shell. The admin shell used to spell its own ids
 * (``admin-user-menu-button``, ``admin-whoami``, ``admin-signout``) back
 * when it had its own component; those are gone. Only ``AdminLayout``'s
 * mobile drawer keeps ``admin-drawer-*`` — that's a different widget.
 */
/**
 * 🚨 Rozbalený panel NENÍ ``role="menu"`` a jeho položky nejsou
 * ``role="menuitem"`` — do KAN-661 byly.
 *
 * ARIA ``menu`` je seznam PŘÍKAZŮ a smí obsahovat jen ``menuitem`` &
 * spol.; tenhle panel ale nese přepínače motivu, akcentu, jazyka,
 * slovníku a dva switche, tedy formulářové prvky. axe to hlásí jako
 * ``aria-required-children`` (wcag2a) a odečítač obrazovky na tom
 * doplácí dvakrát: ohlásí „nabídka, 6 položek" o panelu, kde je jich
 * dvanáct, a radiogroup uvnitř menu neumí odvyprávět vůbec.
 *
 * Odkazy a tlačítka mají vlastní implicitní role, které jsou pro AT
 * lepší než přebitý ``menuitem``. Vazbu spouštěč ↔ panel drží
 * ``aria-expanded`` + ``aria-controls``.
 *
 * Chycené to bylo až ``tests/AccountMenuA11y.test.tsx``: e2e sweepy
 * (``a11y-admin.spec.ts``) čekají na spouštěč a hned skenují, dropdown
 * nikdy neotevřou — pro axe tedy nikdy neexistoval.
 */
const PANEL_ID = "account-menu-panel";

interface AccountMenuProps {
  /**
   * Host-owned sign-out. ``AdminLayout`` passes its own because the
   * platform-admin host mounts no ``/sign-in`` route and has to
   * cross-host to the marketing one. Omitted → the menu logs out and
   * navigates to ``/{lang}`` itself.
   */
  onSignOut?: () => void;
  signOutPending?: boolean;
}

export function AccountMenu({
  onSignOut,
  signOutPending,
}: AccountMenuProps = {}): JSX.Element {
  const { t } = useTranslation("auth");
  const { t: tAdmin } = useTranslation("admin");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const navigate = useNavigate();
  const location = useLocation();
  const shellNav = useShellNav();

  const me = useAuthMe();
  // PR-E2 follow-up: surface pending invitations on the avatar so the
  // invitee finds their CustomerOrg / tenant-team invites without
  // having to deep-link from the email. Gated on ``me.data`` so anon
  // visitors don't pay for an extra fetch on every page.
  const pending = usePendingInvitations({ enabled: !!me.data });
  const pendingCount = pending.data?.length ?? 0;
  // Per-org roles (``OrganizationMember.role``) — owners from self-serve
  // signup keep ``User.role=customer`` globally and would otherwise lose
  // the admin entry the moment they finished signup.
  const { isAdmin } = useIsCurrentOrgAdmin();
  const { isOperator } = useIsCurrentOrgOperator();
  const localLogout = useLogout({
    onSuccess: () => {
      setOpen(false);
      navigate(`/${lang}`);
    },
  });

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close dropdown on outside click + Escape — standard UX for a
  // stateless dropdown with no portal. Listeners are attached only
  // while the menu is open so anon renders stay cheap.
  //
  // 🪤 Escape also puts focus BACK on the trigger. Without it the menu
  // vanishes from under the keyboard and focus falls to <body>, so the
  // next Tab restarts at the top of the page — the person who opened the
  // menu with the keyboard loses their place, and a screen reader is left
  // announcing nothing at all. Mouse users never see the difference,
  // which is exactly why it stayed missing.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Still resolving whoami — tiny pill so the layout doesn't jump.
  if (me.isPending) {
    return (
      <span
        className="inline-block h-8 w-16 rounded-md bg-surface-2"
        aria-hidden="true"
        data-testid="account-menu-loading"
      />
    );
  }

  // 401 (anon) surfaces as isError with ApiError.status=401.
  const isAnon =
    me.isError && me.error instanceof ApiError && me.error.status === 401;

  if (isAnon || !me.data) {
    // Carry the current path as ``redirect_to`` so the magic-link
    // callback returns the user to where they were (cart, checkout,
    // quote detail…) instead of the landing page. The sign-in page
    // itself strips ``/sign-in`` so we never loop.
    const currentPath = `${location.pathname}${location.search}`;
    const isOnSignIn = location.pathname.endsWith("/sign-in");
    const redirectParam =
      !isOnSignIn && currentPath && currentPath !== "/"
        ? `?redirect_to=${encodeURIComponent(currentPath)}`
        : "";
    return (
      <Link
        to={`/${lang}/sign-in${redirectParam}`}
        className={cx(
          "inline-flex items-center rounded-md border border-border-strong bg-surface px-3 py-1.5 text-sm font-medium text-ink-2 shadow-sm",
          "hover:bg-bg",
        )}
        data-testid="account-menu-signin"
      >
        {t("menu.signIn")}
      </Link>
    );
  }

  const email = me.data.email;
  const initials = email ? email.split("@")[0].slice(0, 2).toUpperCase() : null;
  const isPlatformAdmin = !!me.data.is_platform_admin;
  const signOut = onSignOut ?? (() => localLogout.mutate());
  const signingOut = signOutPending ?? localLogout.isPending;
  // Which chrome are we standing in? Drives "Můj účet" (stay inside the
  // admin shell rather than dropping the user onto the storefront) and
  // the "back to the shop" entry.
  const inAdminArea = /^\/[a-z]{2}\/admin(\/|$)/.test(location.pathname);
  const inShopfloor = /^\/[a-z]{2}\/shopfloor(\/|$)/.test(location.pathname);
  const roleLabel = tAdmin(
    isPlatformAdmin
      ? "role.platformAdmin"
      : isAdmin
        ? "role.orgAdmin"
        : isOperator
          ? "role.production"
          : "role.customer",
  );

  return (
    <div ref={wrapperRef} className="relative" data-testid="account-menu">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cx(
          "relative inline-flex items-center gap-1.5 rounded-md border border-border-strong bg-surface px-2 py-1.5 text-sm font-medium text-ink-2 shadow-sm",
          "hover:bg-bg",
        )}
        aria-expanded={open}
        aria-controls={PANEL_ID}
        data-testid="account-menu-button"
      >
        {initials ? (
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-bg">
            {initials}
          </span>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-ink-3" aria-hidden="true">
            <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
          </svg>
        )}
        {/* Email rendered visually-hidden so screen-readers announce
            the signed-in identity (the avatar circle alone is just
            two letters of context). Playwright's text matchers
            also pick it up — the e2e ``customer-sign-in`` spec
            asserts the button's text contains the user's email. */}
        {email && <span className="sr-only">{email}</span>}
        <span aria-hidden="true" className="text-ink-4">▾</span>
        {pendingCount > 0 && (
          <span
            aria-label={t("menu.pendingInvitationsBadge", {
              count: pendingCount,
            })}
            data-testid="account-menu-pending-badge"
            className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold leading-none text-white"
          >
            {pendingCount > 9 ? "9+" : pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div
          id={PANEL_ID}
          className="absolute right-0 z-50 mt-1 w-64 rounded-md border border-border bg-surface p-1 shadow-lg"
          data-testid="account-menu-dropdown"
        >
          {/* 1 — identity */}
          <div className="px-3 py-2" data-testid="account-menu-identity">
            <p className="mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              {roleLabel}
            </p>
            <p className="max-w-[200px] truncate text-xs text-ink-2">{email}</p>
          </div>
          <Divider />

          {/* 2 — notices */}
          {pendingCount > 0 && (
            <>
              <Link
                to={`/${lang}/invitations/accept`}
                onClick={() => setOpen(false)}
                className="flex items-center justify-between rounded px-3 py-2 text-sm font-medium text-ink hover:bg-bg"
                data-testid="account-menu-pending"
              >
                <span>{t("menu.pendingInvitations")}</span>
                <span
                  className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-danger px-1.5 text-[11px] font-semibold leading-none text-white"
                  aria-hidden="true"
                >
                  {pendingCount}
                </span>
              </Link>
              <Divider />
            </>
          )}

          {/* 3 + 4 — context switches and personal pages */}
          <ShellBody
            me={me.data}
            lang={lang}
            shellNav={shellNav}
            isAdmin={isAdmin}
            isOperator={isOperator}
            isPlatformAdmin={isPlatformAdmin}
            inAdminArea={inAdminArea}
            inShopfloor={inShopfloor}
            onClose={() => setOpen(false)}
            t={t}
          />

          {/* 5 — preferences. Theme, accent + language live here for
              signed-in users on every shell (the header keeps language for
              anonymous visitors, who have no menu). Inline help and raw
              JSON only act on admin/operator surfaces, so they're gated on
              having such a role rather than on the current shell.

              🪤 Every row is the same shape: label on the LEFT, control on
              the RIGHT. That is a handoff rule, not a preference — a menu
              where one row stacks its label above the control reads as two
              sections rather than one list, and the eye stops looking for
              the controls in a column. ``VocabularyPreference`` was the
              one that stacked. */}
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <span className="text-sm text-ink-2">{tAdmin("theme.label")}</span>
            <ThemeToggle />
          </div>
          <AccentPreference />
          <LangSwitcher variant="row" onSwitch={() => setOpen(false)} />
          {(isAdmin || isOperator || isPlatformAdmin) && (
            <>
              <VocabularyPreference />
              <HelpToggle />
              <RawJsonToggle />
            </>
          )}
          <Divider />

          {/* 6 — sign out */}
          <button
            type="button"
            onClick={() => {
              signOut();
              setOpen(false);
            }}
            disabled={signingOut}
            className="block w-full rounded px-3 py-2 text-left text-sm text-ink-2 hover:bg-bg disabled:opacity-60"
            data-testid="account-menu-signout"
          >
            {signingOut ? t("menu.signingOut") : t("menu.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Akcentová rodina Ingotu — volba patří k uživateli, ne k obrazovce.
 *
 * Uvnitř obchodu ji přebije brand toho obchodu (``TenantBrandTheme`` sype
 * tytéž čtyři proměnné jako inline styl), takže tenhle přepínač je hlavně
 * pro platform konzoli. Ovládací prvek se ale neschovává: kdyby se
 * skrýval podle shellu, uživatel by ho po přechodu mezi hosty hledal.
 */
function AccentPreference(): JSX.Element {
  const { t } = useTranslation("admin");
  const { choice, setChoice } = useAccent();
  const label = t("accent.label");
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2">
      <span className="text-sm text-ink-2">{label}</span>
      <AccentSwatches
        value={choice}
        onChange={setChoice}
        groupLabel={label}
        optionLabel={(family) => `${label} ${t(`accent.family.${family}`)}`}
      />
    </div>
  );
}

/**
 * „Zobrazení" patří k uživateli, ne ke konkrétní stránce.
 *
 * KAN-796: přepínač se jmenoval „Slovník", dokud měnil jen slova. Teď
 * řídí i sbalení sekcí „Pokročilé" (viz ``lib/vocabulary.ts``), takže
 * jméno musí slibovat zobrazení, ne slovníček.
 */
function VocabularyPreference(): JSX.Element {
  const { t } = useTranslation("admin");
  const vocabulary = useVocabulary();
  const label = t("vocabulary.label", { defaultValue: "Zobrazení" });
  return (
    <div
      className="flex items-center justify-between gap-2 px-3 py-2"
      data-testid="vocabulary-preference"
    >
      <span className="text-sm text-ink-2">{label}</span>
      <div
        className="inline-flex items-center gap-0.5"
        role="radiogroup"
        aria-label={label}
      >
        {VOCABULARY_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={vocabulary.mode === mode}
            disabled={vocabulary.saving}
            onClick={() => vocabulary.setMode(mode)}
            className={cx(
              "min-h-7 rounded px-1.5 text-xs",
              vocabulary.mode === mode ? "bg-ink text-bg" : "text-ink-3 hover:bg-bg",
            )}
          >
            {t(`vocabulary.mode.${mode}`, { defaultValue: mode })}
          </button>
        ))}
      </div>
    </div>
  );
}

function Divider(): JSX.Element {
  return <div className="my-1 border-t border-border" aria-hidden />;
}

const ITEM_STRONG =
  "block rounded px-3 py-2 text-sm font-medium text-ink hover:bg-bg";
const ITEM_PLAIN = "block rounded px-3 py-2 text-sm text-ink-2 hover:bg-bg";

interface ShellBodyProps {
  me: AuthMe;
  lang: string;
  shellNav: ShellNav;
  isAdmin: boolean;
  isOperator: boolean;
  isPlatformAdmin: boolean;
  inAdminArea: boolean;
  inShopfloor: boolean;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function ShellBody(props: ShellBodyProps): JSX.Element {
  switch (props.shellNav.accountMenuVariant) {
    case "platform-slim":
      return <PlatformSlimBody {...props} />;
    case "platform-admin":
      return <PlatformAdminBody {...props} />;
    case "tenant":
    default:
      return <TenantBody {...props} />;
  }
}

/** Cross-host entry into ``admin.<apex>``. Rendered for platform staff on
 *  every shell but the platform-admin one — that's where it leads. */
function PlatformAdminLink({
  lang,
  onClose,
  t,
}: Pick<ShellBodyProps, "lang" | "onClose" | "t">): JSX.Element {
  return (
    <a
      href={buildPlatformAdminUrl(`/${lang}/admin`)}
      target="_blank"
      rel="noreferrer"
      onClick={onClose}
      className={ITEM_STRONG}
      data-testid="account-menu-platform-admin"
    >
      {t("menu.platformAdmin")}
    </a>
  );
}

function TenantBody({
  me,
  lang,
  isAdmin,
  isOperator,
  isPlatformAdmin,
  inAdminArea,
  inShopfloor,
  onClose,
  t,
}: ShellBodyProps): JSX.Element {
  const allTeams = me.customer_org_memberships ?? [];
  // KAN-1908 — firemní účet a schvalování neměly ze storefrontu žádný
  // vchod: registrace týmu žila jen na adrese platformy (se slugem
  // obchodu, který zákazník nezná) a fronta schvalování jen na přímé URL.
  const branding = useTenantBranding({
    enabled: (!inAdminArea && !inShopfloor) || allTeams.length > 0,
  });
  // KAN-2109 — ``/auth/me`` vrací členství napříč obchody. Na webu obchodu
  // jen jeho týmy (jako pokladna, KAN-992); týmy jiných obchodů nabízí
  // platformní shell s přeskokem na jejich doménu. Dokud branding neví,
  // čí je host, neukáže se žádný.
  const teams = allTeams.filter(
    (m) => !!branding.data?.slug && m.provider_slug === branding.data.slug,
  );
  // Odkaz na schvalování jen když ho obchod v plánu má — jinak zákazník
  // skončil na hlášce o tarifu obchodu, která patří majiteli.
  const canApprove = teams.some(
    (m) => m.role === "admin" && m.approvals_enabled === true,
  );
  const companySignupHref =
    !inAdminArea &&
    !inShopfloor &&
    teams.length === 0 &&
    branding.data?.offers_company_accounts &&
    branding.data.slug
      ? buildPlatformUrl(
          `/${lang}/sign-up-team?provider=${encodeURIComponent(branding.data.slug)}`,
        )
      : null;
  // A plain customer has no context switches at all — without this the
  // section's trailing rule would land straight under the identity
  // block's and draw a double line.
  const hasContext =
    (isAdmin && !inAdminArea) ||
    ((isOperator || isAdmin) && !inShopfloor) ||
    inAdminArea ||
    inShopfloor ||
    isPlatformAdmin ||
    teams.length > 0;
  return (
    <>
      {isAdmin && !inAdminArea && (
        <Link
          to={`/${lang}/admin`}
          onClick={onClose}
          className={ITEM_STRONG}
          data-testid="account-menu-admin"
        >
          {t("menu.adminDashboard")}
        </Link>
      )}
      {/* Dílna E6 — operátor (production) má viditelný vchod do terminálu;
          dřív musel znát URL zpaměti (jediný odkaz žil v admin sidebaru). */}
      {(isOperator || isAdmin) && !inShopfloor && (
        <Link
          to={`/${lang}/shopfloor`}
          onClick={onClose}
          className={ITEM_STRONG}
          data-testid="account-menu-shopfloor"
        >
          {t("menu.shopfloor")}
        </Link>
      )}
      {/* The way back out of the admin / Dílna chrome. Both hide the
          storefront header, so without this the only exit was the
          browser's back button. */}
      {(inAdminArea || inShopfloor) && (
        <Link
          to={`/${lang}`}
          onClick={onClose}
          className={ITEM_STRONG}
          data-testid="account-menu-storefront"
        >
          {t("menu.storefront")}
        </Link>
      )}
      {isPlatformAdmin && (
        <PlatformAdminLink lang={lang} onClose={onClose} t={t} />
      )}
      {teams.map((m) => (
        <Link
          key={m.customer_org_id}
          to={`/${lang}/buyer/${m.customer_org_id}`}
          onClick={onClose}
          className={ITEM_PLAIN}
          data-testid={`account-menu-buyer-${m.customer_org_id}`}
        >
          {t("menu.buyerTeam", { name: m.name })}
        </Link>
      ))}
      {canApprove && !inAdminArea && (
        <Link
          to={`/${lang}/approvals`}
          onClick={onClose}
          className={ITEM_PLAIN}
          data-testid="account-menu-approvals"
        >
          {t("menu.approvals")}
        </Link>
      )}
      {hasContext && <Divider />}
      <Link
        // Inside the admin shell the account page has its own route so
        // the operator keeps the admin chrome instead of being dropped
        // onto the storefront. Same component either way.
        to={inAdminArea ? `/${lang}/admin/account` : `/${lang}/account`}
        onClick={onClose}
        className={ITEM_PLAIN}
        data-testid="account-menu-account"
      >
        {t("menu.account")}
      </Link>
      <Link
        to={`/${lang}/account/orders`}
        onClick={onClose}
        className={ITEM_PLAIN}
        data-testid="account-menu-orders"
      >
        {t("menu.orders")}
      </Link>
      <Link
        to={`/${lang}/account/projects`}
        onClick={onClose}
        className={ITEM_PLAIN}
        data-testid="account-menu-projects"
      >
        {t("menu.projects")}
      </Link>
      {companySignupHref && (
        // An <a>, not a <Link> — the team sign-up lives on the platform host.
        <a
          href={companySignupHref}
          onClick={onClose}
          className={ITEM_PLAIN}
          data-testid="account-menu-company-account"
        >
          {t("menu.companyAccount")}
        </a>
      )}
      <Divider />
    </>
  );
}

// Platform-slim body — rendered on forgmatic.com when the user is
// signed-in. The marketing shell does NOT mount /account, /admin,
// /buyer/* etc., so SPA <Link>s would 404. Instead we surface
// cross-host <a> links into the user's actual workspace(s).
//
// 🚨 The session does NOT carry itself — it stopped being true that
// COOKIE_DOMAIN spans the hop, because the cookie is host-only
// outside the platform zone. These links go through ``/auth/hop``,
// which mints a one-time transfer token here and redeems it over
// there. A bare ``buildTenantUrl`` would keep working right up until
// TENANT_DOMAIN moves tenants to their own registrable domain, and
// then land every one of these on a sign-in form.
//
// The no-membership fallback below crosses no host at all: this shell
// mounts ``/account`` itself since KAN-81. It used to build a bare URL
// into the DEMO tenant — a tenant the user is not a member of, so not
// even a transfer token could have rescued it.
function PlatformSlimBody({
  me,
  lang,
  isPlatformAdmin,
  onClose,
  t,
}: ShellBodyProps): JSX.Element {
  const orgs = me.org_memberships ?? [];
  const customerOrgs = me.customer_org_memberships ?? [];
  const hasMemberships = orgs.length > 0 || customerOrgs.length > 0;

  return (
    <>
      {orgs.map((m) => (
        <a
          key={`org-${m.org_id}`}
          href={tenantHopHref(m.slug, `/${lang}/admin`)}
          target="_blank"
          rel="noreferrer"
          onClick={onClose}
          className={ITEM_STRONG}
          data-testid={`account-menu-workspace-org-${m.org_id}`}
        >
          {t("menu.openWorkspace", { name: m.name })}
        </a>
      ))}
      {customerOrgs.map((m) => (
        <a
          key={`customer-${m.customer_org_id}`}
          href={tenantHopHref(
            m.provider_slug,
            `/${lang}/buyer/${m.customer_org_id}`,
          )}
          target="_blank"
          rel="noreferrer"
          onClick={onClose}
          className={ITEM_PLAIN}
          data-testid={`account-menu-workspace-buyer-${m.customer_org_id}`}
        >
          {t("menu.openBuyerWorkspace", { name: m.name })}
        </a>
      ))}
      {!hasMemberships && (
        /* No membership, so there is no workspace to hop to — and nothing
           to mint a token against either. Since KAN-81 the marketing shell
           mounts ``/account`` itself, so this stays on THIS host: a plain
           SPA link, no cookie boundary crossed. It used to build a bare
           cross-host URL that fell back to the DEMO tenant, which showed the
           user their own account wrapped in a stranger's shop. */
        <Link
          to={`/${lang}/account`}
          onClick={onClose}
          className={ITEM_PLAIN}
          data-testid="account-menu-workspace"
        >
          {t("menu.goToWorkspace")}
        </Link>
      )}
      {isPlatformAdmin && (
        <PlatformAdminLink lang={lang} onClose={onClose} t={t} />
      )}
      <Divider />
    </>
  );
}

/**
 * Platform-admin body — ``admin.<apex>``. That shell mounts ``/admin/*``
 * and nothing else, so the only local page is the staffer's own account;
 * everything else has to hop to a tenant host. Until this merge the menu
 * here was empty, which left staff with no in-app way back to their own
 * tenant either.
 */
function PlatformAdminBody({
  me,
  lang,
  onClose,
  t,
}: ShellBodyProps): JSX.Element {
  const orgs = me.org_memberships ?? [];
  return (
    <>
      {orgs.map((m) => (
        <a
          key={`org-${m.org_id}`}
          href={tenantHopHref(m.slug, `/${lang}/admin`)}
          target="_blank"
          rel="noreferrer"
          onClick={onClose}
          className={ITEM_STRONG}
          data-testid={`account-menu-workspace-org-${m.org_id}`}
        >
          {t("menu.openWorkspace", { name: m.name })}
        </a>
      ))}
      {orgs.length > 0 && <Divider />}
      <Link
        to={`/${lang}/admin/account`}
        onClick={onClose}
        className={ITEM_PLAIN}
        data-testid="account-menu-account"
      >
        {t("menu.account")}
      </Link>
      <Divider />
    </>
  );
}
